// ===========================================
// Students Management Page
// ===========================================
// CRUD interface for managing students with class enrollments

import { getStudentAgeGroup, getStudentFamily } from '../../api/users.api';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Alert,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Button,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  ErrorOutline as ExpiredIcon,
  Autorenew as MakeupIcon,
  EmojiEvents as TrophyIcon,
  Discount as DiscountIcon,
  EventBusy as AbsenceIcon,
  Cake as CakeIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import PageHeader from '../../components/common/PageHeader';
import DataTable, { Column } from '../../components/common/DataTable';
import FormModal from '../../components/common/FormModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import StudentBookingsSection from '../../components/admin/StudentBookingsSection';
import DriveFolderPicker from '../../components/googleDrive/DriveFolderPicker';
import {
  ViewStudentDialog,
  EnrollmentItem,
  WaitlistEntriesSection,
  ReportAbsenceDialog,
  DEFAULT_AGE_GROUP_COLOR,
  DEFAULT_STUDENT_FORM_DATA,
  getAvailableLessons,
  type LessonOption,
  type StudentFormData,
} from '../../components/admin/students';
import { AwardAchievementModal } from '../../components/gamification';
import { MediaExposureDot } from '../../components/parent/MediaExposureSelector';
import {
  useAwardAchievement,
  useAwardSpecialAchievement,
  useAchievementDefinitions,
} from '../../hooks/useGamification';
import type { AwardAchievementData, AwardSpecialAchievementData } from '../../types/gamification.types';
import {
  useStudents,
  useFamilies,
  useFamilyImpact,
  useCreateStudent,
  useUpdateStudent,
  useDeleteStudent,
  useStudentEnrollments,
} from '../../hooks/useUsers';
import { usePermissions } from '../../hooks/usePermissions';
import { useLessons, useEnrollStudent, useUnenrollStudent, useRelatedLessons } from '../../hooks/useLessons';
import { useAdminMakeupEligibilities } from '../../hooks/useBooking';
import {
  useStudentInvitationStatus,
  useSendStudentInvitation,
  useResendStudentInvitation,
} from '../../hooks/useInvitations';
import { useCustomDiscounts, useUpdateCustomDiscount } from '../../hooks/useDiscounts';
import { useDiscountCoverageLabel } from '../../hooks/useDiscountCoverageLabel';
import { Student, StudentEnrollment } from '../../api/users.api';
import { getStudentStatusColor, getStudentStatusLabel } from '../../components/admin/students';
import StartWeekSelect from '../../components/admin/lessonDetail/StartWeekSelect';
import AlreadyInvoicedDialog from '../../components/invoices/AlreadyInvoicedDialog';
import { useAlreadyInvoicedPrompt } from '../../hooks/useAlreadyInvoicedPrompt';
import { useBillingNotice } from '../../hooks/useBillingNotice';
import { isPlaceholderBirthDate } from '../../utils/birthdays';

/** The 1 January "birth date unknown" sentinel, judged from the API's ISO string. */
function isPlaceholderBirthDateIso(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  return !Number.isNaN(d.getTime()) && isPlaceholderBirthDate(d);
}

// ===========================================
// COMPONENT
// ===========================================

export default function StudentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: students, isLoading, error } = useStudents();
  const { data: families } = useFamilies();
  const { data: lessons } = useLessons();
  const createMutation = useCreateStudent();
  const updateMutation = useUpdateStudent();
  const deleteMutation = useDeleteStudent();
  // Roles slice 1a: deactivating (DELETE /students/:id) needs people_students.write.
  const { can } = usePermissions();
  const canDeactivateStudent = can('people_students', 'write');
  const enrollMutation = useEnrollStudent();
  const unenrollMutation = useUnenrollStudent();
  // "Already invoiced": a student just enrolled into a term their family was already billed for
  // (term invoicing skips that family, so nothing bills them). Kept for the Back button.
  const alreadyInvoiced = useAlreadyInvoicedPrompt('students-already-invoiced');
  const billingNotice = useBillingNotice();

  // Fetch custom discounts. Pricing data: only with operations_pricing read (whole-slice /qa F-L1,
  // as Families in step 6); assigning needs write.
  const mayReadDiscounts = can('operations_pricing', 'read');
  const mayAssignDiscounts = can('operations_pricing', 'write');
  const { data: discountsData } = useCustomDiscounts({ skip: 0, take: 100 }, { enabled: mayReadDiscounts });
  // #625 D9 (/qa #625 L3): every discount named here says what it covers.
  const coverageOf = useDiscountCoverageLabel(mayReadDiscounts);
  const updateDiscountMutation = useUpdateCustomDiscount();

  // Achievement awarding
  const { data: achievementDefinitions } = useAchievementDefinitions();
  const awardMutation = useAwardAchievement();
  const awardSpecialMutation = useAwardSpecialAchievement();
  const { enqueueSnackbar } = useSnackbar();
  const [awardModalOpen, setAwardModalOpen] = useState(false);
  const [studentToAward, setStudentToAward] = useState<Student | null>(null);
  const [studentToReportAbsence, setStudentToReportAbsence] = useState<Student | null>(null);

  // Form state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [selectedDiscounts, setSelectedDiscounts] = useState<string[]>([]);
  const [formData, setFormData] = useState<StudentFormData>(DEFAULT_STUDENT_FORM_DATA);

  // Family-change guarded state (only relevant when editing).
  // Family dropdown is locked by default in edit mode and requires the admin
  // to flip a switch before they can pick a different family. On submit, if
  // the family actually changed, a confirmation dialog with impact counts is
  // shown before the update is sent.
  const [familyChangeUnlocked, setFamilyChangeUnlocked] = useState(false);
  const [familyChangeConfirmOpen, setFamilyChangeConfirmOpen] = useState(false);
  const originalFamilyId = editingStudent?.familyId || '';
  const familyChangeRequested =
    !!editingStudent && familyChangeUnlocked && (formData.familyId || '') !== originalFamilyId;
  const sourceFamilyId = familyChangeRequested ? originalFamilyId || null : null;
  const targetFamilyId = familyChangeRequested ? formData.familyId || null : null;
  const { data: sourceFamilyImpact } = useFamilyImpact(sourceFamilyId);
  const { data: targetFamilyImpact } = useFamilyImpact(targetFamilyId);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<Student | null>(null);

  // Unenroll confirmation state
  const [unenrollDialogOpen, setUnenrollDialogOpen] = useState(false);
  const [enrollmentToRemove, setEnrollmentToRemove] = useState<StudentEnrollment | null>(null);

  // Lesson selection state
  const [selectedLesson, setSelectedLesson] = useState<LessonOption | null>(null);
  const [enrollInAllTerms, setEnrollInAllTerms] = useState(true);
  // Mid-term start: null = the class's own start. Applies to the chosen class's term only.
  const [enrollStartWeek, setEnrollStartWeek] = useState<number | null>(null);

  // Fetch enrollments when editing a student
  const { data: enrollments, isLoading: isLoadingEnrollments } = useStudentEnrollments(
    editingStudent?.id || null
  );

  // Fetch related lessons for the selected lesson
  const { data: relatedLessons } = useRelatedLessons(selectedLesson?.id || null);

  // Invitation hooks for student portal access. The routes need people_families (read for the
  // status, write to send or resend), #392; without read the status is not even requested.
  const canSeeInvitations = can('people_families', 'read');
  const canSendInvitations = can('people_families', 'write');
  const { data: invitationStatus, isLoading: isLoadingInvitation } = useStudentInvitationStatus(
    canSeeInvitations ? editingStudent?.id || null : null
  );
  const sendInvitation = useSendStudentInvitation();
  const resendInvitation = useResendStudentInvitation();
  const isInvitationPending = sendInvitation.isPending || resendInvitation.isPending;

  // View profile state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingStudent, setViewingStudent] = useState<Student | null>(null);

  // Makeup eligibility filter
  const { data: makeupEligibilities } = useAdminMakeupEligibilities({ unredeemedOnly: true });
  const [showMakeupOnly, setShowMakeupOnly] = useState(false);

  // Get set of student IDs with unredeemed makeup eligibilities
  const studentsWithMakeupIds = new Set(
    makeupEligibilities?.filter((e) => !e.redeemedAt).map((e) => e.studentId) ?? []
  );

  // Students recorded with the 1 January placeholder, so MNM can chase the real
  // date (2026-09-07, Andrew D9). Reached from the birthdays modal's footer link.
  const [showMissingBirthDateOnly, setShowMissingBirthDateOnly] = useState(false);
  // ⚠️ ACTIVE students only, to match the number on the dashboard card and in
  // the modal footer — `useStudents()` returns every row in the school, so an
  // archived placeholder would make the chip say 66 where the card said 61,
  // with no explanation. On a data-quality feature that discrepancy is the
  // whole credibility of the number (/qa 2026-09-07).
  //
  // Residual, deliberately not closed: the backend ALSO filters
  // `deletionStatus: 'ACTIVE'`, which is not on the frontend Student type. On
  // production all 61 placeholders are active and undeleted (measured
  // 2026-09-07), so the two agree today; a soft-deleted placeholder would
  // reappear here. Widen the API contract if that case ever arises.
  const missingBirthDateIds = new Set(
    (students ?? [])
      .filter((s) => s.isActive && isPlaceholderBirthDateIso(s.birthDate))
      .map((s) => s.id)
  );

  // Status filter
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'NEW' | 'ACTIVE' | 'INACTIVE' | 'WAITLISTED'>('ALL');

  // Handle filter query parameter from URL
  useEffect(() => {
    const filter = searchParams.get('filter');
    if (filter === 'makeup') {
      setShowMakeupOnly(true);
      searchParams.delete('filter');
      setSearchParams(searchParams, { replace: true });
    }
    if (filter === 'missing-birth-date') {
      setShowMissingBirthDateOnly(true);
      searchParams.delete('filter');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle edit query parameter from URL
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && students) {
      const studentToEdit = students.find((s) => s.id === editId);
      if (studentToEdit) {
        setEditingStudent(studentToEdit);
        setFamilyChangeUnlocked(false);
        setFormData({
          firstName: studentToEdit.firstName,
          lastName: studentToEdit.lastName,
          birthDate: studentToEdit.birthDate ? studentToEdit.birthDate.split('T')[0] : '',
          familyId: studentToEdit.familyId || '',
          email: studentToEdit.email || '',
          phone: studentToEdit.phone || '',
          notes: studentToEdit.notes || '',
          isActive: studentToEdit.isActive,
        });
        setModalOpen(true);
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, students, setSearchParams]);

  // Get available lessons for enrollment
  const enrolledLessonIds = new Set(enrollments?.map((e) => e.lessonId) ?? []);
  const availableLessons = getAvailableLessons(lessons, enrolledLessonIds);

  // Filter students based on status and makeup eligibility
  const filteredStudents = (students ?? []).filter((student) => {
    // Apply makeup filter
    if (showMakeupOnly && !studentsWithMakeupIds.has(student.id)) {
      return false;
    }

    // Apply "no real birth date" filter
    if (showMissingBirthDateOnly && !missingBirthDateIds.has(student.id)) {
      return false;
    }

    // Apply status filter
    if (statusFilter !== 'ALL' && student.status !== statusFilter) {
      return false;
    }

    return true;
  });

  // Table columns
  const columns: Column<Student>[] = [
    {
      id: 'firstName',
      label: 'Name',
      minWidth: 150,
      searchValue: (row) => `${row.firstName} ${row.lastName}`,
      format: (_, row) => `${row.firstName} ${row.lastName}`,
    },
    {
      id: 'birthDate',
      label: 'Birth Date',
      minWidth: 120,
      format: (value) =>
        value ? new Date(value as string).toLocaleDateString() : '-',
    },
    {
      id: 'ageGroup',
      label: 'Age Group',
      minWidth: 120,
      format: (_, row) => {
        const ageGroup = getStudentAgeGroup(row);
        if (!ageGroup) {
          return <Typography variant="body2" color="text.secondary">—</Typography>;
        }
        const bgColor = ageGroup.color || DEFAULT_AGE_GROUP_COLOR;
        return (
          <Chip
            label={ageGroup.name}
            size="small"
            sx={{ bgcolor: bgColor, color: '#fff' }}
          />
        );
      },
    },
    {
      id: 'family.name',
      label: 'Family',
      minWidth: 150,
      // The id's path never resolves (`GET /students` sends `Family`), so sort
      // and search need their own readers or they silently match nothing.
      sortValue: (row) => getStudentFamily(row)?.name ?? null,
      searchValue: (row) => getStudentFamily(row)?.name ?? '',
      format: (_, row) => {
        const family = getStudentFamily(row);
        const exposure = (family?.mediaExposure ?? 'PENDING') as 'NO' | 'BLUR' | 'FULL' | 'PENDING';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MediaExposureDot exposure={exposure} />
            {family?.name || '-'}
          </Box>
        );
      },
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 120,
      format: (_, row) => {
        const status = (row.status as 'NEW' | 'WAITLISTED' | 'ACTIVE' | 'INACTIVE') || 'NEW';
        return (
          <Chip
            label={getStudentStatusLabel(status)}
            size="small"
            color={getStudentStatusColor(status)}
          />
        );
      },
    },
  ];

  // Handlers
  const handleAdd = () => {
    setEditingStudent(null);
    setFormData(DEFAULT_STUDENT_FORM_DATA);
    setSelectedLesson(null);
    setFamilyChangeUnlocked(false);
    setModalOpen(true);
  };

  const handleEdit = (student: Student) => {
    setEditingStudent(student);

    // Load discounts that apply to this student
    const studentId = student.id;
    const applicableDiscountIds = discountsData?.discounts
      ?.filter((d) => d.applicableStudents.includes(studentId))
      .map((d) => d.id) || [];
    setSelectedDiscounts(applicableDiscountIds);

    setFormData({
      firstName: student.firstName,
      lastName: student.lastName,
      birthDate: student.birthDate ? student.birthDate.split('T')[0] : '',
      familyId: student.familyId || '',
      email: student.email || '',
      phone: student.phone || '',
      notes: student.notes || '',
      isActive: student.isActive,
      status: student.status === 'WAITLISTED' ? 'ACTIVE' : (student.status as 'NEW' | 'ACTIVE' | 'INACTIVE'),
    });
    setSelectedLesson(null);
    setFamilyChangeUnlocked(false);
    setModalOpen(true);
  };

  const handleDelete = (student: Student) => {
    setStudentToDelete(student);
    setDeleteDialogOpen(true);
  };

  const handleView = (student: Student) => {
    setViewingStudent(student);
    setViewModalOpen(true);
  };

  const handleCloseView = () => {
    setViewModalOpen(false);
    setViewingStudent(null);
  };

  const handleEditFromView = () => {
    if (viewingStudent) {
      handleCloseView();
      handleEdit(viewingStudent);
    }
  };

  const handleAwardClick = (student: Student) => {
    awardMutation.reset();
    awardSpecialMutation.reset();
    setStudentToAward(student);
    setAwardModalOpen(true);
  };

  const handleAwardExisting = async (data: AwardAchievementData) => {
    if (!studentToAward) return;
    try {
      await awardMutation.mutateAsync({ studentId: studentToAward.id, data });
      enqueueSnackbar(`Achievement awarded to ${studentToAward.firstName}!`, { variant: 'success' });
    } catch {
      // Error displayed in modal via error prop
    }
  };

  const handleAwardSpecial = async (data: AwardSpecialAchievementData) => {
    if (!studentToAward) return;
    try {
      await awardSpecialMutation.mutateAsync({ studentId: studentToAward.id, data });
      enqueueSnackbar(`Special achievement awarded to ${studentToAward.firstName}!`, { variant: 'success' });
    } catch {
      // Error displayed in modal via error prop
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingStudent(null);
    setSelectedLesson(null);
    setFamilyChangeUnlocked(false);
    setFamilyChangeConfirmOpen(false);
  };

  const performStudentUpdate = async () => {
    if (!editingStudent) return;

    // Only include familyId in the payload when the admin has explicitly
    // unlocked the family change. Otherwise we leave familyId untouched.
    const familyIdPayload = familyChangeRequested
      ? { familyId: formData.familyId ? formData.familyId : null }
      : {};

    await updateMutation.mutateAsync({
      id: editingStudent.id,
      data: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        birthDate: formData.birthDate || null,
        ...familyIdPayload,
        email: formData.email || null,
        phone: formData.phone || null,
        notes: formData.notes || null,
        isActive: formData.isActive,
        status: (formData.status as 'NEW' | 'ACTIVE' | 'INACTIVE') || 'ACTIVE',
      },
    });

    // Update discount assignments
    const studentId = editingStudent.id;
    const allDiscounts = mayAssignDiscounts ? discountsData?.discounts || [] : [];

    for (const discount of allDiscounts) {
      const isCurrentlyAssigned = discount.applicableStudents.includes(studentId);
      const shouldBeAssigned = selectedDiscounts.includes(discount.id);

      if (isCurrentlyAssigned !== shouldBeAssigned) {
        const newApplicableStudents = shouldBeAssigned
          ? [...discount.applicableStudents, studentId]
          : discount.applicableStudents.filter((id) => id !== studentId);

        await updateDiscountMutation.mutateAsync({
          id: discount.id,
          data: {
            applicableStudents: newApplicableStudents,
            name: discount.name,
            value: discount.value,
            isPercent: discount.isPercent,
          },
        });
      }
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingStudent) {
        // Family change requires explicit confirmation before we save anything.
        if (familyChangeRequested) {
          setFamilyChangeConfirmOpen(true);
          return;
        }
        await performStudentUpdate();
      } else {
        await createMutation.mutateAsync({
          firstName: formData.firstName,
          lastName: formData.lastName,
          birthDate: formData.birthDate || undefined,
          familyId: formData.familyId || undefined,
          email: formData.email || undefined,
          phone: formData.phone || undefined,
          notes: formData.notes || undefined,
        });
      }
      handleCloseModal();
    } catch {
      // Error is handled by mutation
    }
  };

  const handleConfirmFamilyChange = async () => {
    try {
      await performStudentUpdate();
      handleCloseModal();
    } catch {
      // Error is handled by mutation; leave the modal open so the admin can retry.
      setFamilyChangeConfirmOpen(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (studentToDelete) {
      try {
        await deleteMutation.mutateAsync(studentToDelete.id);
        setDeleteDialogOpen(false);
        setStudentToDelete(null);
      } catch {
        // Error is handled by mutation
      }
    }
  };

  const handleEnrollStudent = async () => {
    if (!editingStudent || !selectedLesson) return;

    try {
      const shouldEnrollInAllTerms = relatedLessons && relatedLessons.length > 0 && enrollInAllTerms;
      const result = await enrollMutation.mutateAsync({
        lessonId: selectedLesson.id,
        studentId: editingStudent.id,
        enrollInAllTerms: shouldEnrollInAllTerms,
        startWeek: enrollStartWeek,
      });
      alreadyInvoiced.show(result.alreadyInvoiced);
      billingNotice('enrol', result.invoicesMayNeedAttention);
      setSelectedLesson(null);
      setEnrollInAllTerms(true);
      setEnrollStartWeek(null);
    } catch {
      // Error is handled by mutation
    }
  };

  const handleUnenrollClick = (enrollment: StudentEnrollment) => {
    setEnrollmentToRemove(enrollment);
    setUnenrollDialogOpen(true);
  };

  const handleConfirmUnenroll = async () => {
    if (!editingStudent || !enrollmentToRemove) return;

    try {
      await unenrollMutation.mutateAsync({
        lessonId: enrollmentToRemove.lessonId,
        studentId: editingStudent.id,
      });
      setUnenrollDialogOpen(false);
      setEnrollmentToRemove(null);
    } catch {
      // Error is handled by mutation
    }
  };

  const isFormLoading = createMutation.isPending || updateMutation.isPending;
  const isDeleteLoading = deleteMutation.isPending;
  const isEnrollLoading = enrollMutation.isPending;
  const isUnenrollLoading = unenrollMutation.isPending;

  return (
    <Box>
      <PageHeader
        title="Students"
        subtitle="Manage student records and class enrolments"
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Students' },
        ]}
        actionLabel="Add Student"
        onAction={handleAdd}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load students. Please try again.
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {/* Status Filter */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'ALL' | 'NEW' | 'ACTIVE' | 'INACTIVE' | 'WAITLISTED')}
            label="Status"
          >
            <MenuItem value="ALL">All Students</MenuItem>
            <MenuItem value="NEW">New Students</MenuItem>
            <MenuItem value="ACTIVE">Active Students</MenuItem>
            <MenuItem value="WAITLISTED">Waitlisted Students</MenuItem>
            <MenuItem value="INACTIVE">Inactive Students</MenuItem>
          </Select>
        </FormControl>

        {/* No-birth-date filter (2026-09-07) */}
        {missingBirthDateIds.size > 0 && (
          <Chip
            icon={<CakeIcon />}
            label={`No Birth Date (${missingBirthDateIds.size})`}
            onClick={() => setShowMissingBirthDateOnly(!showMissingBirthDateOnly)}
            color={showMissingBirthDateOnly ? 'warning' : 'default'}
            variant={showMissingBirthDateOnly ? 'filled' : 'outlined'}
            sx={{ cursor: 'pointer' }}
            data-testid="missing-birth-date-filter"
          />
        )}

        {/* Makeup Filter */}
        <Chip
          icon={<MakeupIcon />}
          label={`Make-up Eligible (${studentsWithMakeupIds.size})`}
          onClick={() => setShowMakeupOnly(!showMakeupOnly)}
          color={showMakeupOnly ? 'warning' : 'default'}
          variant={showMakeupOnly ? 'filled' : 'outlined'}
          sx={{ cursor: 'pointer' }}
        />
      </Box>

      <DataTable
        columns={columns}
        data={filteredStudents}
        loading={isLoading}
        renderExtraActions={(student) => (
          <>
            <Tooltip title="Report Absence">
              <IconButton size="small" onClick={() => setStudentToReportAbsence(student)}>
                <AbsenceIcon fontSize="small" sx={{ color: 'error.main' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Award Achievement">
              <IconButton size="small" onClick={() => handleAwardClick(student)}>
                <TrophyIcon fontSize="small" sx={{ color: 'warning.main' }} />
              </IconButton>
            </Tooltip>
          </>
        )}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={canDeactivateStudent ? handleDelete : undefined}
        canDelete={(student) => !student.isActive}
        deleteDisabledTooltip="Set student to inactive before deleting"
        emptyMessage={
          filteredStudents.length === 0
            ? "No students match the selected filters."
            : "No students found. Click 'Add Student' to create one."
        }
        searchPlaceholder="Search students..."
      />

      {/* Add/Edit Modal */}
      <FormModal
        open={modalOpen}
        title={editingStudent ? 'Edit Student' : 'Add Student'}
        submitLabel={editingStudent ? 'Update' : 'Create'}
        loading={isFormLoading}
        onSubmit={handleSubmit}
        onClose={handleCloseModal}
        maxWidth="md"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* Basic Info */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="First Name"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Last Name"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
              fullWidth
            />
          </Stack>
          <TextField
            label="Birth Date"
            type="date"
            value={formData.birthDate}
            onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
            helperText="Age group will be calculated automatically"
          />
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl fullWidth disabled={!!editingStudent && !familyChangeUnlocked}>
              <InputLabel>Family</InputLabel>
              <Select
                value={formData.familyId}
                onChange={(e) => setFormData({ ...formData, familyId: e.target.value })}
                label="Family"
              >
                <MenuItem value="">
                  <em>No family</em>
                </MenuItem>
                {families?.map((family) => (
                  <MenuItem key={family.id} value={family.id}>
                    {family.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {editingStudent && (
              <FormControlLabel
                control={
                  <Switch
                    checked={familyChangeUnlocked}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setFamilyChangeUnlocked(next);
                      // If the admin re-locks, snap the dropdown back to the original.
                      if (!next) {
                        setFormData((f) => ({ ...f, familyId: originalFamilyId }));
                      }
                    }}
                  />
                }
                label="Allow family change"
                sx={{ whiteSpace: 'nowrap' }}
              />
            )}
          </Stack>
          {editingStudent && familyChangeUnlocked && (
            <Alert severity="warning" sx={{ mt: -1 }}>
              Reassigning a student to a different family does not move historical
              invoices or payment plans &mdash; those stay attached to the previous
              family. You will be asked to confirm before the change is saved.
            </Alert>
          )}
          <TextField
            label="Notes"
            placeholder="Any additional notes about this student..."
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            fullWidth
            multiline
            rows={3}
          />

          {/* Student Status - Only show when editing */}
          {editingStudent && (
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status || 'ACTIVE'}
                label="Status"
                onChange={(e) => setFormData({ ...formData, status: e.target.value as 'NEW' | 'ACTIVE' | 'INACTIVE' })}
              >
                <MenuItem value="NEW">New Student</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
              </Select>
            </FormControl>
          )}

          <Divider sx={{ my: 1 }} />

          {/* Contact & Portal Access */}
          <Typography variant="subtitle2" color="text.secondary">
            Contact & Portal Access
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Email"
              type="email"
              placeholder="student@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
              helperText="Required for portal invitation"
            />
            <TextField
              label="Phone"
              placeholder="(04) 1234-5678"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
            />
          </Stack>

          {/* Portal Access - Show when editing */}
          {editingStudent && canSeeInvitations && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Portal Access
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {!formData.email ? (
                  <Typography variant="body2" color="text.secondary">
                    Save email first, then invite
                  </Typography>
                ) : isLoadingInvitation ? (
                  <CircularProgress size={20} />
                ) : invitationStatus?.hasAccount ? (
                  <Chip
                    icon={<CheckCircleIcon fontSize="small" />}
                    label="Has Account"
                    color="success"
                    size="small"
                  />
                ) : invitationStatus?.status === 'PENDING' ? (
                  <>
                    <Chip
                      icon={<PendingIcon fontSize="small" />}
                      label="Invitation Pending"
                      color="warning"
                      size="small"
                    />
                    {invitationStatus.canResend && canSendInvitations && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={isInvitationPending ? <CircularProgress size={14} /> : <RefreshIcon />}
                        onClick={() => editingStudent && resendInvitation.mutate(editingStudent.id)}
                        disabled={isInvitationPending}
                      >
                        Resend
                      </Button>
                    )}
                  </>
                ) : invitationStatus?.status === 'EXPIRED' ? (
                  <>
                    <Chip
                      icon={<ExpiredIcon fontSize="small" />}
                      label="Invitation Expired"
                      color="error"
                      size="small"
                    />
                    {invitationStatus.canResend && canSendInvitations && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={isInvitationPending ? <CircularProgress size={14} /> : <RefreshIcon />}
                        onClick={() => editingStudent && resendInvitation.mutate(editingStudent.id)}
                        disabled={isInvitationPending}
                      >
                        Resend
                      </Button>
                    )}
                  </>
                ) : invitationStatus?.canInvite && canSendInvitations ? (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={isInvitationPending ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
                    onClick={() => editingStudent && sendInvitation.mutate(editingStudent.id)}
                    disabled={isInvitationPending}
                  >
                    Invite to Portal
                  </Button>
                ) : invitationStatus?.status === 'NO_EMAIL' ? (
                  <Typography variant="body2" color="text.secondary">
                    Save email first, then invite
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No portal access
                  </Typography>
                )}
              </Box>
            </Paper>
          )}

          {/* Google Drive Folder - Only show when editing */}
          {editingStudent && (
            <>
              <Divider sx={{ my: 1 }} />

              <Typography variant="subtitle2" color="text.secondary">
                Google Drive Folder
              </Typography>
              <DriveFolderPicker studentId={editingStudent.id} />
            </>
          )}

          {/* Enrollments Section - Only show when editing */}
          {editingStudent && (
            <>
              <Divider sx={{ my: 1 }} />

              <Typography variant="subtitle2" color="text.secondary">
                Class Enrollments ({enrollments?.length || 0})
              </Typography>

              {/* Current Enrollments */}
              {isLoadingEnrollments ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : enrollments && enrollments.length > 0 ? (
                <Stack spacing={1}>
                  {enrollments.map((enrollment) => (
                    <EnrollmentItem
                      key={enrollment.id}
                      enrollment={enrollment}
                      onRemove={() => handleUnenrollClick(enrollment)}
                      isLoading={isUnenrollLoading}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  Not enrolled in any classes
                </Typography>
              )}

              {/* Add to Class */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Autocomplete
                    options={availableLessons}
                    getOptionLabel={(option) => option.label}
                    value={selectedLesson}
                    onChange={(_, newValue) => {
                      setSelectedLesson(newValue);
                      setEnrollStartWeek(null); // a start week belongs to the class it was picked for
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Add to class"
                        placeholder="Search for a class..."
                        size="small"
                      />
                    )}
                    sx={{ flex: 1 }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                  <Tooltip title="Enrol in class">
                    <span>
                      <IconButton
                        color="primary"
                        onClick={handleEnrollStudent}
                        disabled={!selectedLesson || isEnrollLoading}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'primary.dark' },
                          '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                        }}
                      >
                        {isEnrollLoading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <AddIcon />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>

                {/* Mid-term start: options run from the class's first week to its last */}
                {selectedLesson?.term && (
                  <StartWeekSelect
                    id="student-enrol-start-week"
                    lesson={{
                      startWeek: selectedLesson.startWeek,
                      endWeek: selectedLesson.endWeek,
                      dayOfWeek: selectedLesson.dayOfWeek ?? null,
                    }}
                    term={selectedLesson.term}
                    value={enrollStartWeek}
                    onChange={setEnrollStartWeek}
                    disabled={isEnrollLoading}
                  />
                )}

                {/* Enroll in all terms checkbox */}
                {selectedLesson && relatedLessons && relatedLessons.length > 0 && (
                  <Box sx={{ ml: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={enrollInAllTerms}
                          onChange={(e) => setEnrollInAllTerms(e.target.checked)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Enroll in all terms ({relatedLessons.length + 1} terms:{' '}
                          {relatedLessons.map((l) => l.termName).join(', ')})
                        </Typography>
                      }
                    />
                    {selectedLesson.term && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                        Starting week applies to {selectedLesson.term.name} only.
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Bookings Section */}
              <StudentBookingsSection
                studentId={editingStudent.id}
                studentName={`${editingStudent.firstName} ${editingStudent.lastName}`}
              />

              {/* Waitlist Section */}
              <WaitlistEntriesSection
                studentId={editingStudent.id}
                isAdmin={true}
              />

              {/* Applicable Discounts */}
              {mayReadDiscounts && discountsData?.discounts && discountsData.discounts.filter(d => d.scope === 'STUDENT').length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DiscountIcon sx={{ fontSize: 16 }} />
                      Applicable Discounts
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      {mayAssignDiscounts
                        ? 'Select which custom discounts apply to this student'
                        : 'View only: changing which discounts apply needs Pricing & Products (edit) access.'}
                    </Typography>
                    <Stack spacing={1}>
                      {discountsData.discounts.filter(d => d.scope === 'STUDENT').map((discount) => (
                        <Paper key={discount.id} variant="outlined" sx={{ p: 1.5 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedDiscounts.includes(discount.id)}
                                disabled={!mayAssignDiscounts}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedDiscounts([...selectedDiscounts, discount.id]);
                                  } else {
                                    setSelectedDiscounts(selectedDiscounts.filter((id) => id !== discount.id));
                                  }
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {discount.name}
                                  {' '}
                                  <Chip
                                    label={discount.isPercent ? `${discount.value}%` : `$${discount.value}`}
                                    size="small"
                                    color="primary"
                                    sx={{ ml: 0.5 }}
                                  />
                                  {' '}
                                  <Chip
                                    label={discount.status}
                                    size="small"
                                    color={discount.status === 'ACTIVE' ? 'success' : 'default'}
                                  />
                                </Typography>
                                {coverageOf(discount) && (
                                  <Typography variant="caption" color="text.secondary" display="block" data-testid="student-discount-covers">
                                    Covers {coverageOf(discount)}
                                  </Typography>
                                )}
                                {discount.description && (
                                  <Typography variant="caption" color="text.secondary">
                                    {discount.description}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      </FormModal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Deactivate Student"
        message={`Are you sure you want to deactivate ${studentToDelete?.firstName} ${studentToDelete?.lastName}?`}
        confirmLabel="Deactivate"
        confirmColor="error"
        loading={isDeleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />

      {/* Unenroll Confirmation */}
      <ConfirmDialog
        open={unenrollDialogOpen}
        title="Remove from Class"
        message={`Are you sure you want to remove ${editingStudent?.firstName} from "${enrollmentToRemove?.lesson.name}"?`}
        confirmLabel="Remove"
        confirmColor="error"
        loading={isUnenrollLoading}
        onConfirm={handleConfirmUnenroll}
        onCancel={() => {
          setUnenrollDialogOpen(false);
          setEnrollmentToRemove(null);
        }}
      />

      {/* Family Change Confirmation */}
      <Dialog
        open={familyChangeConfirmOpen}
        onClose={() => updateMutation.isPending ? undefined : setFamilyChangeConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Move student to a different family?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            <strong>{editingStudent?.firstName} {editingStudent?.lastName}</strong> will be moved
            from <strong>{sourceFamilyImpact?.name ?? 'No family'}</strong> to{' '}
            <strong>{targetFamilyImpact?.name ?? 'No family'}</strong>.
          </Typography>

          {sourceFamilyImpact && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Current family: {sourceFamilyImpact.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                After this move:
                {' '}{Math.max(0, sourceFamilyImpact.studentCount - 1)} students,{' '}
                {sourceFamilyImpact.parentCount} parents.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Stays attached to this family (does NOT move):
                {' '}{sourceFamilyImpact.totalInvoiceCount} invoice{sourceFamilyImpact.totalInvoiceCount === 1 ? '' : 's'},{' '}
                {sourceFamilyImpact.paymentPlanCount} payment plan{sourceFamilyImpact.paymentPlanCount === 1 ? '' : 's'}.
              </Typography>
              {Math.max(0, sourceFamilyImpact.studentCount - 1) === 0 &&
                sourceFamilyImpact.parentCount === 0 && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    This family will have no students and no parents after the move.
                    You can clean it up separately from the Families page.
                  </Alert>
                )}
            </Paper>
          )}

          {targetFamilyImpact ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                New family: {targetFamilyImpact.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Currently has {targetFamilyImpact.studentCount} student{targetFamilyImpact.studentCount === 1 ? '' : 's'},{' '}
                {targetFamilyImpact.parentCount} parent{targetFamilyImpact.parentCount === 1 ? '' : 's'}{' '}
                ({targetFamilyImpact.primaryParentCount} primary).
              </Typography>
              {targetFamilyImpact.primaryParentCount === 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Target family has no primary parent. Hybrid pre-booking will fail
                  for this student until a primary parent is set on the family.
                </Alert>
              )}
            </Paper>
          ) : (
            <Alert severity="info">
              The student will have no family attached after this change.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setFamilyChangeConfirmOpen(false)}
            disabled={updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmFamilyChange}
            color="warning"
            variant="contained"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Moving...' : 'Move student'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Student Profile Modal */}
      <ViewStudentDialog
        open={viewModalOpen}
        student={viewingStudent}
        onClose={handleCloseView}
        onEdit={handleEditFromView}
      />

      {/* Report Absence Modal */}
      <ReportAbsenceDialog
        open={!!studentToReportAbsence}
        studentId={studentToReportAbsence?.id || ''}
        studentName={studentToReportAbsence ? `${studentToReportAbsence.firstName} ${studentToReportAbsence.lastName}` : ''}
        onClose={() => setStudentToReportAbsence(null)}
      />

      {/* Award Achievement Modal */}
      <AwardAchievementModal
        open={awardModalOpen}
        onClose={() => {
          setAwardModalOpen(false);
          setStudentToAward(null);
        }}
        studentName={studentToAward ? `${studentToAward.firstName} ${studentToAward.lastName}` : ''}
        definitions={achievementDefinitions || []}
        onAwardExisting={handleAwardExisting}
        onAwardSpecial={handleAwardSpecial}
        isAwarding={awardMutation.isPending || awardSpecialMutation.isPending}
        error={awardMutation.error?.message || awardSpecialMutation.error?.message || null}
      />

      {/* Enrolled into a term the family was already invoiced for */}
      <AlreadyInvoicedDialog entries={alreadyInvoiced.entries} onClose={alreadyInvoiced.clear} />
    </Box>
  );
}
